import { CSSProperties } from "react";
import { css, StyleSheet } from "aphrodite";
import { Skeleton } from "antd";
import { useLocation } from "react-router-dom";
import { mobileView } from "../commonCss/commonCss";

type PlaceholderShape = "catalog" | "productDetail" | "page";

const catalogPath = "/BuyJewelleries";
const productDetailPathPrefix = "/product/";
const catalogCardCount = 6;
const catalogChipCount = 5;
const productDetailParagraphRows = 6;
const pageParagraphRows = 8;
// Skeleton.Node sizes its inner block only through `style`; a className also lands on the outer element.
const cardImageStyle: CSSProperties = { width: "100%", height: "auto", aspectRatio: "1 / 1" };
const productImageStyle: CSSProperties = { width: "100%", height: "clamp(260px, 42vw, 500px)" };

/** Placeholder shaped like the page at the current location, for use while that page loads. */
export function RoutePlaceholder() {
    const { pathname } = useLocation();
    return <PagePlaceholder shape={getPlaceholderShape(pathname)} />;
}

export function PagePlaceholder({ shape }: { shape: PlaceholderShape }) {
    return (
        <div role="status" aria-label="Loading page" className={css(styles.page)}>
            {shape === "catalog" && <CatalogPlaceholder />}
            {shape === "productDetail" && <ProductDetailPlaceholder />}
            {shape === "page" && <GenericPagePlaceholder />}
        </div>
    );
}

/** Placeholder product cards, for a product grid whose products are still loading. */
export function ProductCardsPlaceholder() {
    return (
        <div role="status" aria-label="Loading products">
            <ProductCardGrid />
        </div>
    );
}

function CatalogPlaceholder() {
    return (
        <div className={css(styles.catalog)}>
            <div className={css(styles.alignEnd)}>
                <Skeleton.Button active shape="round" />
            </div>
            <Skeleton.Input active block size="large" className={css(styles.searchBar)} />
            <Skeleton.Input active size="small" className={css(styles.heading)} />
            <div className={css(styles.chipRow)}>
                {Array.from({ length: catalogChipCount }, (_, chip) => (
                    <Skeleton.Button key={chip} active shape="round" size="small" />
                ))}
            </div>
            <ProductCardGrid />
        </div>
    );
}

function ProductCardGrid() {
    return (
        <div className={css(styles.cardGrid)}>
            {Array.from({ length: catalogCardCount }, (_, card) => (
                <div key={card}>
                    <PlaceholderBlock style={cardImageStyle} />
                    <Skeleton active title={false} paragraph={{ rows: 2 }} className={css(styles.cardText)} />
                </div>
            ))}
        </div>
    );
}

function ProductDetailPlaceholder() {
    return (
        <div className={css(styles.productDetail)}>
            <div className={css(styles.productImage)}>
                <PlaceholderBlock style={productImageStyle} />
            </div>
            <div className={css(styles.productInfo)}>
                <Skeleton active paragraph={{ rows: productDetailParagraphRows }} />
            </div>
        </div>
    );
}

function GenericPagePlaceholder() {
    return (
        <div className={css(styles.genericPage)}>
            <Skeleton active paragraph={{ rows: pageParagraphRows }} />
        </div>
    );
}

function PlaceholderBlock({ style }: { style: CSSProperties }) {
    return (
        <Skeleton.Node active rootClassName={css(styles.blockRoot)} style={style}>
            <></>
        </Skeleton.Node>
    );
}

function getPlaceholderShape(pathname: string): PlaceholderShape {
    if (pathname.startsWith(productDetailPathPrefix)) {
        return "productDetail";
    }
    return pathname === catalogPath ? "catalog" : "page";
}

const styles = StyleSheet.create({
    page: {
        width: "100%",
        boxSizing: "border-box",
    },
    catalog: {
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        maxWidth: "1360px",
        margin: "0 auto",
        padding: "0.5rem 1rem 2rem",
    },
    alignEnd: {
        display: "flex",
        justifyContent: "flex-end",
    },
    searchBar: {
        maxWidth: "420px",
    },
    heading: {
        width: "200px",
    },
    chipRow: {
        display: "flex",
        gap: "0.5rem",
        overflow: "hidden",
    },
    cardGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gap: "1.5rem",
        [mobileView]: {
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "0.75rem",
        },
    },
    blockRoot: {
        display: "block",
        width: "100%",
    },
    cardText: {
        marginTop: "0.75rem",
    },
    productDetail: {
        display: "flex",
        gap: "20px",
        maxWidth: "1440px",
        margin: "24px auto",
        padding: "20px clamp(12px, 4vw, 32px)",
        [mobileView]: {
            flexDirection: "column",
            margin: "10px auto",
            padding: "8px",
        },
    },
    productImage: {
        flex: "1 1 50%",
        maxWidth: "760px",
        minWidth: 0,
        [mobileView]: {
            maxWidth: "none",
        },
    },
    productInfo: {
        flex: 1,
        minWidth: 0,
    },
    genericPage: {
        maxWidth: "1100px",
        margin: "0 auto",
        padding: "2rem 1rem",
    },
});
